import { createClient, Entry } from "contentful"
import Settings from "../../utils/CarrotConfig"
import { IPageGuidance, ITalkToCarrotCountry, ITilePage } from "../../contentful-types"
import ContentTypes from "../../utils/ContentTypes"
import { nestedRefs } from "./utils/contentfulClientUtils"
import { reportError, reportErrorMessage } from "utils/ErrorReporting"

enum StaticAssetID {
  TALK_TO_CARROT_LOCATION_GUIDANCE = "3ByERJy0buFOMf6f9BU6RQ",
  TALK_TO_CARROT_NEED_HELP_GUIDANCE = "6HZUR0v8YJYCGeFG5ncCtm",
  TALK_TO_CARROT_PROVIDER_ELIGIBILITY = "3Tnl2nccRTKkk4oMC4ZmxB",
  TALK_TO_CARROT_REFRESH_CARROT_PLAN = "4oPn08X5v4mH61LsrqKT3H",
  TALK_TO_CARROT_GET_YOUR_CARROT_PLAN = "1n7FzTcMpdeJmMYir1FvHB"
}

const getContentfulClient = () =>
  createClient({
    space: Settings.CONTENTFUL_SPACE_ID,
    accessToken: Settings.CONTENTFUL_ACCESS_TOKEN,
    host: Settings.CONTENTFUL_PREVIEW_MODE === "true" ? "preview.contentful.com" : "cdn.contentful.com"
  })

const getTalkToCarrotLocations = async (): Promise<ITalkToCarrotCountry[]> => {
  const client = getContentfulClient()
  const { items } = await client.getEntries({
    content_type: ContentTypes.TALK_TO_CARROT_COUNTRY,
    include: 1
  }) /* eslint-disable-line @typescript-eslint/camelcase */
  return items as ITalkToCarrotCountry[]
}

const getTilePageById = async (id: string): Promise<ITilePage> => {
  const result = (await getEntryById(id)) as ITilePage
  await nestedRefs(result, getEntryById)
  return result
}

const getEntryById = async (id: string): Promise<Entry<unknown>> => {
  const client = getContentfulClient()
  try {
    const entry = await client.getEntry(id, { include: 10 })
    return entry
  } catch (err) {
    if (err && err.sys) {
      const {
        sys: { id: errorId }
      } = err
      if (errorId === "NotFound") {
        reportErrorMessage(
          `Contentful resource not found for entityId: ${id}, this may just be a work in progress and in a draft state`
        )
        return null
      }
    }
    reportError(err)
    return null
  }
}

const getGuidance = async (entryId: StaticAssetID): Promise<IPageGuidance> => {
  const item = (await getEntryById(entryId)) as IPageGuidance
  if (!item) {
    reportErrorMessage(`No Contentful page guidance for entityId: ${entryId}`)
  }
  return item
}

const getTalkToCarrotLocationGuidance = async (): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_LOCATION_GUIDANCE)

const getTalkToCarrotNeedHelpGuidance = async (): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_NEED_HELP_GUIDANCE)

const getTalkToCarrotProviderEligibilityGuidance = async (): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_PROVIDER_ELIGIBILITY)

const getTalkToCarrotRefreshCarrotPlanGuidance = async (): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_REFRESH_CARROT_PLAN)

const getTalkToCarrotGetYourCarrotPlanGuidance = async (): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_GET_YOUR_CARROT_PLAN)

export {
  getContentfulClient,
  getTalkToCarrotLocations,
  getTalkToCarrotLocationGuidance,
  getTilePageById,
  getTalkToCarrotNeedHelpGuidance,
  getTalkToCarrotProviderEligibilityGuidance,
  getTalkToCarrotRefreshCarrotPlanGuidance,
  getTalkToCarrotGetYourCarrotPlanGuidance
}
